import { useCallback, useEffect, useState } from "react";
import {
  authGeneration,
  authTransitionPending,
  beginAuthTransition,
  finishAuthTransition,
  isCurrentAuth,
} from "./lib/auth-lifecycle";
import { subscribeFreshness } from "../shared/freshness";
export class ApiFailure extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
  }
}
/** The UI language, sent so the API can answer errors in the same language. */
export function requestLanguage(): "ja" | "zh" {
  try {
    return localStorage.getItem("juyici_language") === "ja" ? "ja" : "zh";
  } catch {
    return "zh";
  }
}
export async function api<T>(
  path: string,
  method = "GET",
  body?: unknown,
  options: { idempotencyKey?: string } = {},
): Promise<T> {
  const transition =
    method === "POST" &&
    ["/auth/verify", "/auth/wechat", "/auth/logout"].includes(path);
  // Requests issued with an unsettled cookie cannot report identity loss,
  // even if their response arrives after this login/logout has completed.
  const canSignalAuthLoss = !transition && !authTransitionPending();
  const ticket = transition ? beginAuthTransition() : authGeneration();
  try {
    const response = await fetch("/api" + path, {
      method,
      credentials: "include",
      headers: {
        "Accept-Language": requestLanguage(),
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(options.idempotencyKey
          ? { "Idempotency-Key": options.idempotencyKey }
          : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401 && isCurrentAuth(ticket) && canSignalAuthLoss)
        window.dispatchEvent(new Event("juyici:auth-lost"));
      throw new ApiFailure(
        data.error?.code ?? "UNKNOWN",
        data.error?.message ?? "Request failed",
      );
    }
    if (transition && !isCurrentAuth(ticket))
      throw new ApiFailure(
        "AUTH_CHANGED",
        requestLanguage() === "ja"
          ? "ログイン状態が変わりました。もう一度お試しください。"
          : "登录状态已变化，请重试。",
      );
    return data as T;
  } finally {
    if (transition) finishAuthTransition(ticket);
  }
}
export function useResource<T>(path: string) {
  const [data, setData] = useState<T | null>(null),
    [error, setError] = useState<Error | null>(null),
    [loading, setLoading] = useState(true),
    [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api<T>(path)
      .then((value) => {
        if (active) setData(value);
      })
      .catch((e) => {
        if (active) {
          setData(null);
          setError(e);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [path, revision]);
  const reload = useCallback(() => setRevision((v) => v + 1), []);
  useEffect(() => {
    const stop = subscribeFreshness({ window, document }, reload);
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") reload();
    }, 30_000);
    return () => {
      stop();
      clearInterval(timer);
    };
  }, [reload]);
  return { data, error, loading, reload };
}
